import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { userLogService } from "../services/service";

const EMPTY_PARAMS = {};
const USER_LOGS_KEY = ["user-logs"];

export const useUserLogs = (initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [actionFilter, setActionFilter] = useState("all");
    const [userFilter, setUserFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        search: searchTerm || undefined,
        action: actionFilter !== "all" ? actionFilter : undefined,
        username: userFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [currentPage, pageSize, searchTerm, actionFilter, userFilter, startDate, endDate, initialParams]);

    const query = useQuery({
        queryKey: [...USER_LOGS_KEY, "list", params],
        queryFn: () => userLogService.getAll(params),
        staleTime: Infinity,
    });

    const statsQuery = useQuery({
        queryKey: [...USER_LOGS_KEY, "stats"],
        queryFn: () => userLogService.getStats(),
        staleTime: 60_000,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: USER_LOGS_KEY }),
        [queryClient]
    );

    const responseData = query.data || {};
    const meta = responseData.meta || {};

    return {
        userLogs: responseData.data || [],
        pagination: {
            total: meta.total ?? 0,
            last_page: meta.last_page ?? 1,
            current_page: meta.current_page ?? 1,
            per_page: meta.per_page ?? pageSize,
            from: meta.from ?? 0,
            to: meta.to ?? 0,
        },
        loading: query.isLoading || query.isFetching,
        error: query.error,
        stats: statsQuery.data?.data || null,
        statsLoading: statsQuery.isLoading,

        searchTerm,
        setSearchTerm,
        actionFilter,
        setActionFilter,
        userFilter,
        setUserFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,

        refresh: query.refetch,
        refreshStats: statsQuery.refetch,
        invalidate,
    };
};

// Hook for a single user log
export const useUserLog = (userlogId) => {
    const query = useQuery({
        queryKey: [...USER_LOGS_KEY, "detail", userlogId],
        queryFn: () => userLogService.getById(userlogId),
        enabled: Boolean(userlogId),
        staleTime: Infinity,
    });

    return {
        userLog: query.data?.data || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        refresh: query.refetch,
    };
};

// Hook for logs by a specific user (username)
export const useUserLogsByUser = (username, initialParams = EMPTY_PARAMS) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        ...initialParams,
    }), [currentPage, pageSize, initialParams]);

    const query = useQuery({
        queryKey: [...USER_LOGS_KEY, "user", username, params],
        queryFn: () => userLogService.getByUser(username, params),
        enabled: Boolean(username),
        staleTime: Infinity,
    });

    const responseData = query.data || {};
    const meta = responseData.meta || {};

    return {
        userLogs: responseData.data || [],
        pagination: {
            total: meta.total ?? 0,
            last_page: meta.last_page ?? 1,
            current_page: meta.current_page ?? 1,
            per_page: meta.per_page ?? pageSize,
            from: meta.from ?? 0,
            to: meta.to ?? 0,
        },
        loading: query.isLoading || query.isFetching,
        error: query.error,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        refresh: query.refetch,
    };
};